import { useEffect } from "react";

const DEFAULT_TITLE =
  "Bilateral Calm — Bilateral Stimulation, Bilateral Sound & EMDR-Inspired Practices for Anxiety, Insomnia, Stress & Trauma";
const DEFAULT_DESCRIPTION =
  "Bilateral Calm uses bilateral stimulation and bilateral sound — the same alternating left-right principles behind EMDR (Eye Movement Desensitization and Reprocessing) — to help you self-soothe anxiety, insomnia, stress, and trauma responses. Start a 5-day free trial.";
const SITE_ORIGIN = "https://bilateralcalm.com";

// Find or create a <meta> tag of the given kind/name and set its content.
// kind=`property` for og: tags, `name` for everything else.
const setMeta = (kind, key, content) => {
  if (!content) return;
  let el = document.head.querySelector(`meta[${kind}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(kind, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
};

const setCanonical = (url) => {
  let el = document.head.querySelector('link[rel="canonical"]');
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    document.head.appendChild(el);
  }
  el.setAttribute("href", url);
};

/**
 * Lightweight SEO hook — no react-helmet needed. Updates title, meta
 * description, canonical, and OG/Twitter previews per page so Google,
 * iMessage, Slack, and X all render meaningful summaries when one of our
 * marketing pages is shared.
 *
 *   useSeo({ title: "Pricing — Bilateral Calm", description: "...", path: "/pricing" });
 */
export const useSeo = ({
  title,
  description,
  path,
  image,
  noindex = false,
} = {}) => {
  useEffect(() => {
    const finalTitle = title || DEFAULT_TITLE;
    const finalDescription = description || DEFAULT_DESCRIPTION;
    const finalUrl = `${SITE_ORIGIN}${path || "/"}`;
    const finalImage = image || `${SITE_ORIGIN}/images/home-hero-reeds.webp`;

    // Snapshot the values we override so we can restore them on unmount.
    // This keeps the homepage defaults intact when the user navigates back.
    const prev = {
      title: document.title,
      description: document.head.querySelector('meta[name="description"]')?.getAttribute("content"),
      canonical: document.head.querySelector('link[rel="canonical"]')?.getAttribute("href"),
      ogTitle: document.head.querySelector('meta[property="og:title"]')?.getAttribute("content"),
      ogDesc: document.head.querySelector('meta[property="og:description"]')?.getAttribute("content"),
      ogUrl: document.head.querySelector('meta[property="og:url"]')?.getAttribute("content"),
      ogImage: document.head.querySelector('meta[property="og:image"]')?.getAttribute("content"),
      twTitle: document.head.querySelector('meta[name="twitter:title"]')?.getAttribute("content"),
      twDesc: document.head.querySelector('meta[name="twitter:description"]')?.getAttribute("content"),
      twImage: document.head.querySelector('meta[name="twitter:image"]')?.getAttribute("content"),
      robots: document.head.querySelector('meta[name="robots"]')?.getAttribute("content"),
    };

    document.title = finalTitle;
    setMeta("name", "description", finalDescription);
    setCanonical(finalUrl);
    setMeta("property", "og:title", finalTitle);
    setMeta("property", "og:description", finalDescription);
    setMeta("property", "og:url", finalUrl);
    setMeta("property", "og:image", finalImage);
    setMeta("name", "twitter:title", finalTitle);
    setMeta("name", "twitter:description", finalDescription);
    setMeta("name", "twitter:image", finalImage);
    setMeta("name", "robots", noindex ? "noindex, nofollow" : "index, follow, max-image-preview:large, max-snippet:-1");

    return () => {
      // Restore previous values when the component unmounts so subsequent
      // pages start from a known-good baseline.
      document.title = prev.title || DEFAULT_TITLE;
      if (prev.description) setMeta("name", "description", prev.description);
      if (prev.canonical) setCanonical(prev.canonical);
      if (prev.ogTitle) setMeta("property", "og:title", prev.ogTitle);
      if (prev.ogDesc) setMeta("property", "og:description", prev.ogDesc);
      if (prev.ogUrl) setMeta("property", "og:url", prev.ogUrl);
      if (prev.ogImage) setMeta("property", "og:image", prev.ogImage);
      if (prev.twTitle) setMeta("name", "twitter:title", prev.twTitle);
      if (prev.twDesc) setMeta("name", "twitter:description", prev.twDesc);
      if (prev.twImage) setMeta("name", "twitter:image", prev.twImage);
      if (prev.robots) setMeta("name", "robots", prev.robots);
    };
  }, [title, description, path, image, noindex]);
};

export default useSeo;
